import { useMemo } from 'react';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import useFilteredCanvas from '@components/MainStage/hooks/useFilteredCanvas';
import { SubLayersListByMode } from '@constants/canvas/general';
import { Modes } from '@constants/canvas/layers';
import { ExportLayerKeys } from '@constants/entities/reports';
import { useProject } from '@context/Project/ProjectProvider';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';
import useProjectData from '@hooks/useProjectData';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectCanvasNodesQuery } from '@store/services/nodes';
import { useGenerateNodesReportMutation } from '@store/services/reports';
import {
  getExportEntityIds,
  getOtherModeEntityIds,
  hideNodesOutsideFocus,
} from '@views/ReportsModel/exportScope';
import { getLayersReportFileName } from '@views/ReportsModel/helpers';
import useExportState from '@views/ReportsModel/hooks/useExportState';

const useExportLayers = (projectId: string, layers: ExportLayerKeys[]) => {
  const { isError, setError, fileContent, setFileContent } = useExportState();
  const [generateNodesReport, { isLoading, reset }] =
    useGenerateNodesReportMutation();

  const {
    toolbox: { mode, domains },
  } = useProject();

  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = useProjectFilters();
  const [{ subscription }] = useProjectData();

  const { nodes } = useFilteredCanvas({ applyCollapse: false });
  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();

  const currentModeSubLayers = useMemo(
    () => new Set<string>(mode ? SubLayersListByMode[mode] : []),
    [mode],
  );

  const otherMode =
    mode === Modes.BusinessAlignment ? Modes.Controls : Modes.BusinessAlignment;

  const shouldFetchOtherMode =
    !!projectId &&
    !!mode &&
    mode !== Modes.RiskManagement &&
    !isFocusActive &&
    (subscription?.modes ?? []).includes(otherMode);

  const { data: otherModeData, isError: isOtherModeError } =
    useGetProjectCanvasNodesQuery(
      shouldFetchOtherMode ? { projectId, mode: otherMode } : skipToken,
    );

  const handleGenerate = async () => {
    try {
      if (shouldFetchOtherMode && (isOtherModeError || !otherModeData)) {
        setError(true);

        return;
      }

      const currentModeIds = getExportEntityIds(
        hideNodesOutsideFocus(nodes, isFocusActive, focusedIds),
      );

      const otherModeIds = isFocusActive
        ? []
        : getOtherModeEntityIds({
            nodes: otherModeData?.nodes ?? [],
            currentModeSubLayers,
            domains,
            modelFilters: {
              validationStatus,
              reviewStatus,
              operationalStatuses,
              operatingModelStatuses,
              partners,
              ciaFilters,
            },
          });

      const ids = Array.from(new Set([...currentModeIds, ...otherModeIds]));

      const excelData = await generateNodesReport({
        projectId,
        ids,
        layers,
      }).unwrap();

      setFileContent(excelData);
    } catch (e) {
      setError(true);
    }
  };

  const fileName = getLayersReportFileName(layers);

  return {
    isError,
    fileContent,
    isLoading,
    setFileContent,
    setError,
    handleGenerate,
    fileName,
    reset,
  };
};

export default useExportLayers;
